/* eslint-disable import/first */
import React, { PureComponent, FormEvent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Type, ButtonSecondary, FormInput } from '@spotify-internal/encore-creator-web';
import { spacer24, screenXsMax } from '@spotify-internal/encore-foundation';
import { copy } from '../../../vendor/Clipboard';
import { trackEvent } from '../../../lib/Tracking';
import { logger } from '../../../lib/Api/index';
import { ShareAction } from '../../../types';
import { FormattedMessage } from 'react-intl';

const ShareRow = styled.div`
  display: grid;
  grid-template-columns: 450px auto;
  margin-bottom: 32px;

  @media (max-width: ${screenXsMax}) {
    grid-template-columns: 1fr;
    grid-row-gap: ${spacer24};
  }
`;

const LeftContainer = styled.div`
  max-width: 400px;
`;

interface Props {
  podcastId: string;
  copy(value: string): Promise<void>;
}

export class PodcastShare extends PureComponent<Props> {
  static propTypes = {
    podcastId: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired,
  };

  static defaultProps = {
    copy,
  };

  state = {
    shareUrlHasBeenCopied: false,
  };

  _timeout?: number = undefined;

  componentWillUnmount() {
    clearTimeout(this._timeout);
    this.removeCopyListener();
  }

  get shareUrl() {
    return `https://open.spotify.com/show/${this.props.podcastId}`;
  }

  trackCopyLink = (type: ShareAction) => {
    // @ts-ignore
    trackEvent({ category: 'share', action: 'copy-link', label: this.shareUrl });
    logger.podcastShareAction(this.props.podcastId, type);
  };

  handleCopyClick = () => {
    this.props.copy(this.shareUrl).then(() => {
      this.trackCopyLink('copy-button');
      this.setState({ shareUrlHasBeenCopied: true });
      this._timeout = window.setTimeout(
        /*istanbul ignore next */
        () => this.setState({ shareUrlHasBeenCopied: false }),
        3000,
      );
    });
  };

  logDownloadBadge = () => {
    // @ts-ignore
    trackEvent({ category: 'share', action: 'download-badges', label: this.shareUrl });
    logger.podcastShareAction(this.props.podcastId, 'download-badges');
  };

  handleOnClick = (event: FormEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  handleOnSelect =
    /* istanbul ignore next */
    () => {
      window.addEventListener('keydown', this._copyListener);
    };

  removeCopyListener =
    /* istanbul ignore next */
    () => {
      window.removeEventListener('keydown', this._copyListener);
    };

  /**
   * Note about all the `istanbul ignore next`, @testing-library/react can't properly simulate
   * the copy pasting going on here, the suggesting from KCD is to run this type of test in
   * an actual browser. There is a test for the copy button itself which does cover the setTimeout
   * and trackEvent calls.
   */
  _copyListener =
    /* istanbul ignore next */
    event => {
      if ((event.ctrlKey || event.metaKey) && event.key.match(/c/)) {
        this.trackCopyLink('copy-event');
      }
    };

  render() {
    return (
      <div data-qa="podcast-share">
        <Type as="p" variant={Type.heading3}>
          <FormattedMessage id="podcast-share.share-title" defaultMessage="Share" />
        </Type>
        <Type as="p" variant={Type.body1} weight={Type.bold}>
          <FormattedMessage
            id="podcast-details.podcast-link"
            defaultMessage="Podcast link"
            description="Label for text box that contains the URL of the podcast on Spotify"
          />
        </Type>
        <ShareRow>
          <LeftContainer>
            <FormInput
              data-testid="share-url"
              type="text"
              value={this.shareUrl}
              onClick={this.handleOnClick}
              onSelect={this.handleOnSelect}
              onBlur={this.removeCopyListener}
              readOnly
            />
          </LeftContainer>
          <div>
            <ButtonSecondary
              buttonSize="md"
              onClick={this.handleCopyClick}
              disabled={this.state.shareUrlHasBeenCopied}
              data-testid="copy-button"
            >
              {/* istanbul ignore next */ this.state.shareUrlHasBeenCopied ? (
                <FormattedMessage
                  id="podcast-details.podcast-link-copied"
                  defaultMessage="Copied!"
                />
              ) : (
                <FormattedMessage
                  id="podcast-details.podcast-link-copy"
                  defaultMessage="Copy Link"
                />
              )}
            </ButtonSecondary>
          </div>
        </ShareRow>

        <Type as="p" variant={Type.body1} weight={Type.bold} style={{ paddingTop: '32px' }}>
          <FormattedMessage
            id="podcast-details.podcast-badges"
            defaultMessage="Podcast badges"
            description="Title of section where a user can download images of the Spotify logo"
          />
        </Type>
        <form method="get" action="/resources/spotify-podcast-badges.zip">
          <ShareRow>
            <LeftContainer>
              <Type as="p" variant={Type.body2} color="gray60">
                <FormattedMessage
                  id="podcast-details.podcast-badges-details"
                  defaultMessage="Badges are in .svg, .png and .eps formats."
                />
              </Type>
              <img
                src="/images/spotify-podcast-badge-quad.png"
                width="338px"
                height="88px"
                alt="Listen on Spotify badge"
              />
            </LeftContainer>
            <div style={{ alignSelf: 'flex-end' }}>
              <ButtonSecondary buttonSize="md" onClick={this.logDownloadBadge}>
                <FormattedMessage
                  id="podcast-details.podcast-badges-download"
                  defaultMessage="Download"
                />
              </ButtonSecondary>
            </div>
          </ShareRow>
        </form>
      </div>
    );
  }
}
